/**
 * TUVI - AuthContext
 * Manejo de autenticación. Sesión válida hasta el día siguiente del login.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { GRAPHQL_URL } from '../config';

const AuthContext = createContext(null);

const DAY_CHECK_INTERVAL = 30 * 60 * 1000; // Verificar cambio de día cada 30 minutos

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const justLoggedInRef = useRef(false);

  // Cerrar sesión
  const logout = useCallback(async (reason = 'manual') => {
    try {
      const storedToken = token || localStorage.getItem('tuvi_token');

      if (storedToken) {
        await fetch(GRAPHQL_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${storedToken}`
          },
          body: JSON.stringify({ query: 'mutation { logout }' })
        }).catch(() => {});
      }
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('tuvi_token');
      localStorage.removeItem('tuvi_user');
      localStorage.removeItem('tuvi_expires');
      localStorage.removeItem('tuvi_login_date');

      if (reason === 'expired') {
        console.log('Sesión expirada');
      } else if (reason === 'day_changed') {
        console.log('Sesión expirada - nuevo día');
      }
    }
  }, [token]);

  // Verificar si la sesión sigue válida
  const verifySession = useCallback(async () => {
    // Si acabamos de hacer login, no verificar de nuevo (evita loop 401)
    if (justLoggedInRef.current) {
      justLoggedInRef.current = false;
      setLoading(false);
      return true;
    }

    const storedToken = localStorage.getItem('tuvi_token');

    if (!storedToken) {
      setLoading(false);
      return false;
    }

    // Verificar si cambió el día respecto al login
    const loginDate = localStorage.getItem('tuvi_login_date');
    if (loginDate) {
      const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
      if (hoy > loginDate) {
        await logout('day_changed');
        setLoading(false);
        return false;
      }
    }

    try {
      const response = await fetch(GRAPHQL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storedToken}`
        },
        body: JSON.stringify({
          query: `{ me { id username nombreCompleto rol activo permisos { modulo submodulo tieneAcceso } } }`
        })
      });

      const json = await response.json();

      if (json.data?.me) {
        const gqlUser = json.data.me;
        const permisos = {};
        (gqlUser.permisos || []).forEach(p => {
          // Módulos sin subcategorías (submodulo vacío) usan solo el módulo como key
          const key = p.submodulo ? `${p.modulo}.${p.submodulo}` : p.modulo;
          permisos[key] = p.tieneAcceso;
        });
        const userData = {
          id: gqlUser.id,
          username: gqlUser.username,
          nombre_completo: gqlUser.nombreCompleto,
          rol: gqlUser.rol,
          activo: gqlUser.activo,
          permisos
        };
        setUser(userData);
        setToken(storedToken);
        localStorage.setItem('tuvi_user', JSON.stringify(userData));
        return true;
      } else {
        const storedUser = localStorage.getItem('tuvi_user');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
          setToken(storedToken);
          return true;
        }
        await logout('expired');
        return false;
      }
    } catch (error) {
      console.error('Error verificando sesión:', error);
      // Si hay error de red, usar datos locales (no cerrar sesión)
      const storedUser = localStorage.getItem('tuvi_user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
        return true;
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Login
  const login = useCallback((userData, authToken) => {
    justLoggedInRef.current = true;
    setUser(userData);
    setToken(authToken);
    // Guardar la fecha del login (zona Colombia)
    const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
    localStorage.setItem('tuvi_login_date', hoy);
  }, []);

  // Verificar sesión al cargar
  useEffect(() => {
    verifySession();
  }, [verifySession]);

  // Verificar expiración periódicamente (cambio de día)
  useEffect(() => {
    if (!token) return;

    const checkExpiration = () => {
      const loginDate = localStorage.getItem('tuvi_login_date');
      if (loginDate) {
        const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
        if (hoy > loginDate) {
          logout('day_changed');
        }
      }
    };

    const interval = setInterval(checkExpiration, DAY_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [token, logout]);

  // Helper: verificar si el usuario tiene acceso a un submódulo
  const tienePermiso = useCallback((modulo, submodulo) => {
    if (!user) return false;
    const permisos = user.permisos || {};
    return !!permisos[`${modulo}.${submodulo}`];
  }, [user]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    tienePermiso
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;
